import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faUserSlash,
    faEllipsisH,
    faPrint,
    faDownload,
    faReceipt,
    faArrowUpRightFromSquare,
    faEnvelope,
} from '@fortawesome/free-solid-svg-icons';

import { NavLink } from "react-router-dom";

import DateCell from "../Listing/DateCell";
import fitColumns from "../Listing/fitColumns";

import PatientImage from "../Patient/PatientImage";

import InputText from "../Form/Fields/InputText";
import InputSliderRange from "../Form/Fields/InputSliderRange";
import InputPatient from "../Form/Fields/InputPatient";
import PaymentMethodBadge from './PaymentMethodBadge';
import { TotalCell } from './orderColumns';

export const formatAmount = (val) =>
    new Intl.NumberFormat("fr-MA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val ?? 0);

// Default view: # · Facture · Total · Méthode · Articles · Date · Patient · Vente (Reçu / Rendu hidden)
const buildInvoiceColumns = ({ handleViewPdf, downloadInvoice, printReceipt, openSendEmail, totalMax = 1000 }) => [
    {
        Header: "#",
        accessor: "id",
        sortable: true,
        className: "text-center",
        filterable: { component: InputText },
        Cell: (row) => <span style={{ fontSize: 14, fontWeight: 500, color: "#9ca3af" }}>{row.id}</span>,
    },
    {
        Header: "Facture",
        accessor: "invoiceNumber",
        minWidth: "180px",
        filterable: { component: InputText },
        Cell: (row) => (
            <div style={{ whiteSpace: 'nowrap' }}>
                {/* same reference style as the orders listing (orderColumns.js) */}
                <NavLink to={`/invoices/${row.id}`} className="cell-name" style={{ fontSize: 13.5 }}>{row.invoiceNumber}</NavLink>
            </div>
        ),
    },
    {
        Header: "Total",
        accessor: "total",
        filterable: { component: InputSliderRange, min: 0, max: totalMax, step: 1, unit: "dhs" },
        sortable: true,
        activeColumn: true,
        className: "text-end",
        // same cell as the orders listing, right-aligned so the amounts line up
        Cell: (row) => (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <TotalCell total={row.totalAmount} />
            </div>
        ),
    },
    {
        Header: "Méthode",
        accessor: "paymentMethod",
        Cell: (row) => row.paymentMethod
            ? <PaymentMethodBadge method={row.paymentMethod} />
            : <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>,
    },
    {
        Header: "Articles",
        accessor: "itemNames",
        className: "text-center",
        Cell: (row) => {
            const count = row.itemNames?.length ?? 0;
            if (count === 0) return <span className="cell-sub">—</span>;
            return (
                <span style={{
                    fontSize: 12, fontWeight: 600, color: '#374151',
                    background: '#f3f4f6', border: '1px solid #e5e7eb',
                    borderRadius: 20, padding: '2px 10px',
                }}>
                    {count}
                </span>
            );
        },
    },
    {
        // exact date + time, relative age underneath (same as the sales listing)
        Header: "Date",
        accessor: "invoiceCreatedAt",
        Cell: (row) => <DateCell date={row.invoiceCreatedAt} />,
    },
    {
        Header: "Patient",
        accessor: "patientId",
        filterable: { component: InputPatient },
        Cell: (row) => {
            if (row.patient != null) {
                return (
                    <div className="d-flex align-items-center gap-2">
                        <PatientImage patient={row.patient} size={32} />
                        <div>
                            <NavLink className="cell-name" style={{ fontSize: 13 }} to={`/patient/${row.patient.id}`}>
                                {row.patient.firstName}{row.patient.lastName ? ` ${row.patient.lastName[0]}.` : ''}
                            </NavLink>
                        </div>
                    </div>
                );
            }
            return (
                <div className="d-flex align-items-center gap-2">
                    <span style={{
                        width: 32, height: 32, borderRadius: "50%",
                        background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                        <FontAwesomeIcon icon={faUserSlash} style={{ color: "#9ca3af", fontSize: 13 }} />
                    </span>
                    <span className="cell-sub" style={{ marginTop: 0 }}>Visiteur</span>
                </div>
            );
        },
    },
    {
        Header: "Vente",
        accessor: "orderNumber",
        className: "text-center",
        filterable: { component: InputText },
        Cell: (row) => row.orderId
            ? (
                <NavLink
                    to={`/order/${row.orderId}`}
                    style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 28, height: 28, borderRadius: 8,
                        background: '#f3f4f6', color: '#6b7280',
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#2563eb'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#6b7280'; }}
                    data-tooltip-id="tooltip"
                    data-tooltip-content={row.orderNumber}
                >
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: 12 }} />
                </NavLink>
            )
            : <span style={{ color: '#6b7280', whiteSpace: 'nowrap' }}>{row.orderNumber ?? '—'}</span>,
    },
    {
        Header: "Reçu / Rendu",
        accessor: "cashGiven",
        visible: false, // secondary: in "Colonnes"
        Cell: (row) => {
            if (row.paymentMethod !== 'CASH' || row.cashGiven == null) {
                return <span style={{ color: '#d1d5db' }}>—</span>;
            }
            const change = (parseFloat(row.cashGiven) - parseFloat(row.totalAmount)).toFixed(2);
            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>
                        {formatAmount(row.cashGiven)} <span style={{ fontSize: 11, color: '#9ca3af' }}>dhs</span>
                    </span>
                    <span style={{ fontSize: 11, color: parseFloat(change) >= 0 ? '#059669' : '#dc2626', fontWeight: 500 }}>
                        rendu {formatAmount(change)} dhs
                    </span>
                </div>
            );
        },
    },
    {
        Header: "",
        accessor: "actions",
        className: "col-actions",
        Cell: (row) => (
            <div className="cell-dropdown">
                <button className="cell-actions-btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                    <FontAwesomeIcon icon={faEllipsisH} />
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                    <li>
                        <button className="dropdown-item" onClick={() => printReceipt(row.id)}>
                            <FontAwesomeIcon icon={faReceipt} className="me-2 text-muted" />
                            Imprimer reçu
                        </button>
                    </li>
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                        <button className="dropdown-item" onClick={() => handleViewPdf(row.id)}>
                            <FontAwesomeIcon icon={faPrint} className="me-2 text-muted" />
                            Imprimer la facture
                        </button>
                    </li>
                    <li>
                        <button className="dropdown-item" onClick={() => downloadInvoice(row.id)}>
                            <FontAwesomeIcon icon={faDownload} className="me-2 text-muted" />
                            Télécharger en PDF
                        </button>
                    </li>
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                        <button className="dropdown-item" onClick={() => openSendEmail(row)}>
                            <FontAwesomeIcon icon={faEnvelope} className="me-2 text-muted" />
                            Envoyer par email
                        </button>
                    </li>
                </ul>
            </div>
        ),
    },
];

// only the invoice number stretches, the others fit their content (fitColumns)
export const getInvoiceColumns = ({ handleViewPdf, downloadInvoice, printReceipt, openSendEmail, totalMax = 1000 }) => fitColumns(buildInvoiceColumns({ handleViewPdf, downloadInvoice, printReceipt, openSendEmail, totalMax }), "invoiceNumber");
